import { REDUX_ACTIONS, GAME_ID, TABS } from 'a/directorsCut/data/constants';

const initState = {
  [TABS.CHARACTERS]: [], // array of objects like {id: "characterId", position: {}}
  [TABS.PROPS]: [], // array of objects like {id: "propId", position: {}}
  [TABS.EMOTIONS]: [], // array of objects like {id: "emotionId", position: {}}
  tomatoedCharacters: [],
  tomatoedProps: [],
  tomatoedEmotions: [],
  hintCharacters: [],
  hintProps: [],
  hintEmotions: [],
  disabledItemIds: [], // array of item ids that are correct and should be disabled during correction
  focusedId: '', // id of element selected on canvas
};

const canvasReducer = (state = initState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.ADD_CHARACTER_TO_CANVAS: {
      const diff = { x: 0, y: 0 };
      let prevItemPos = { x: 0, y: 0 };
      const characters = [payload];
      state[TABS.CHARACTERS].forEach((item) => {
        if (item.characterId !== payload.characterId) {
          characters.push(item);
        } else if (payload.position.isDefault) {
          characters[0].position = { ...item.position };
        } else {
          prevItemPos = { ...item.position };
          diff.x = prevItemPos.x - payload.position.x;
          diff.y = prevItemPos.y - payload.position.y;
        }
      });
      return {
        ...state,
        [TABS.CHARACTERS]: characters,
        // if the previous character had a tought bubble
        // update it's position to maintain its relative pos to the
        // character
        [TABS.EMOTIONS]: state[TABS.EMOTIONS].map(item => ({
          ...item,
          position: item.characterId === payload.characterId
            ? { x: item.position.x - diff.x, y: item.position.y - diff.y }
            : item.position,
        })),
      };
    }

    case REDUX_ACTIONS.ADD_PROP_TO_CANVAS: {
      return {
        ...state,
        [TABS.PROPS]: [
          ...state[TABS.PROPS],
          payload,
        ],
      };
    }

    case REDUX_ACTIONS.ADD_EMOTION_TO_CANVAS: {
      const { position: oldPos } = state[TABS.EMOTIONS].find(item => item.characterId === payload.characterId) || {};
      return {
        ...state,
        [TABS.EMOTIONS]: [
          ...state[TABS.EMOTIONS].filter(item => item.characterId !== payload.characterId),
          payload.position.isDefault && oldPos ? { ...payload, position: oldPos } : payload,
        ],
      };
    }

    case REDUX_ACTIONS.UPDATE_CHARACTER_POSITION: {
      const diff = { x: 0, y: 0 };
      return {
        ...state,
        [TABS.CHARACTERS]: state[TABS.CHARACTERS].map(item => {
          const isMatch = item.id === payload.id;
          if (isMatch) {
            diff.x = item.position.x - payload.position.x;
            diff.y = item.position.y - payload.position.y;
          }
          return ({
            ...item,
            position: item.id === payload.id ? payload.position : item.position,
          });
        }),
        [TABS.EMOTIONS]: state[TABS.EMOTIONS].map(item => ({
          ...item,
          position: item.characterId === payload.characterId
            ? { x: item.position.x - diff.x, y: item.position.y - diff.y }
            : item.position,
        })),
      };
    }

    case REDUX_ACTIONS.UPDATE_PROP_POSITION:
      return {
        ...state,
        [TABS.PROPS]: state[TABS.PROPS].map(item => ({
          ...item,
          position: item.id === payload.id ? payload.position : item.position,
        })),
      };

    case REDUX_ACTIONS.UPDATE_EMOTION_POSITION:
      return {
        ...state,
        [TABS.EMOTIONS]: state[TABS.EMOTIONS].map(item => ({
          ...item,
          position: item.id === payload.id ? payload.position : item.position,
        })),
      };

    case REDUX_ACTIONS.REMOVE_CHARACTER: {
      const characters = [];
      state[TABS.CHARACTERS].forEach((item) => {
        if (item.id !== payload.id) {
          characters.push(item);
        }
      });
      const emotions = [];
      // if the empotion linked is with the character, remove it along with it.
      state[TABS.EMOTIONS].forEach((item) => {
        if (item.characterId !== payload.characterId) {
          emotions.push(item);
        }
      });
      return {
        ...state,
        [TABS.CHARACTERS]: characters,
        [TABS.EMOTIONS]: emotions,
      };
    }

    case REDUX_ACTIONS.REMOVE_PROPS: {
      const props = [];
      state[TABS.PROPS].forEach((item) => {
        if (item.id !== payload.id) {
          props.push(item);
        }
      });
      return {
        ...state,
        [TABS.PROPS]: props,
      };
    }

    case REDUX_ACTIONS.REMOVE_EMOTION: {
      const emotions = [];
      state[TABS.EMOTIONS].forEach((item) => {
        if (item.id !== payload.id) {
          emotions.push(item);
        }
      });
      return {
        ...state,
        [TABS.EMOTIONS]: emotions,
      };
    }

    case REDUX_ACTIONS.ASSOCIATE_EMOTION: {
      return {
        ...state,
        [TABS.EMOTIONS]: state[TABS.EMOTIONS].map(item => ({
          ...item,
          characterId: item.id === payload.emotionId ? payload.characterId : item.characterId,
        })),
      };
    }

    case REDUX_ACTIONS.SET_TOMATOED_CHARACTER: {
      return {
        ...state,
        tomatoedCharacters: [
          ...state.tomatoedCharacters,
          payload,
        ],
      };
    }

    case REDUX_ACTIONS.SET_TOMATOED_PROP: {
      return {
        ...state,
        tomatoedProps: [
          ...state.tomatoedProps,
          payload,
        ],
      };
    }

    case REDUX_ACTIONS.SET_TOMATOED_EMOTION: {
      return {
        ...state,
        tomatoedEmotions: [
          ...state.tomatoedEmotions,
          payload,
        ],
      };
    }

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM:
    case REDUX_ACTIONS.ROUND_END: {
      return {
        ...initState,
      };
    }

    case REDUX_ACTIONS.UPDATE_HINT: {
      const { wrongElements } = payload;
      const { hintCharacters, hintProps, hintEmotions } = state;
      const hintSelectedCharacterIds = hintCharacters.filter(el => el.isSelected).map(el => el.id);
      const hintSelectedPropIds = hintProps.filter(el => el.isSelected).map(el => el.id);
      const hintSelectedEmotionIds = hintEmotions
        .filter(el => el.isSelected)
        .map(el => `${el.id}_said-by-${el.characterId}`);
      return {
        ...state,
        hintCharacters: [
          ...wrongElements[TABS.CHARACTERS].filter(el => !hintSelectedCharacterIds.includes(el.id)),
          ...hintCharacters.filter(el => el.isSelected),
        ],
        hintProps: [
          ...wrongElements[TABS.PROPS].filter(el => !hintSelectedPropIds.includes(el.id)),
          ...hintProps.filter(el => el.isSelected),
        ],
        hintEmotions: [
          ...wrongElements[TABS.EMOTIONS].filter(
            el => !hintSelectedEmotionIds.includes(`${el.id}_said-by-${el.characterId}`),
          ),
          ...hintEmotions.filter(el => el.isSelected),
        ],
      };
    }

    case REDUX_ACTIONS.SET_CANVAS_DISABLED_ITEMS: {
      const { hintCharacters, hintProps, hintEmotions } = state;
      const canvasItemIds = [
        ...state[TABS.CHARACTERS].map(({ id }) => id),
        ...state[TABS.PROPS].map(({ id }) => id),
        ...state[TABS.EMOTIONS].map(({ id }) => id),
      ];
      const hintItemIds = [
        ...hintCharacters.filter(el => el.isSelected).map(el => el.id),
        ...hintProps.filter(el => el.isSelected).map(el => el.id),
        ...hintEmotions.filter(el => el.isSelected).map(el => el.id),
      ];
      const disabledItemIds = canvasItemIds.filter(x => !hintItemIds.includes(x));
      return {
        ...state,
        disabledItemIds,
      };
    }

    case REDUX_ACTIONS.FOCUS_ELEMENT_ID:
      return {
        ...state,
        focusedId: payload,
      };

    case REDUX_ACTIONS.BLUR_ELEMENT_ID:
      return {
        ...state,
        focusedId: state.focusedId === payload ? '' : state.focusedId,
      };

    default:
      return state;
  }
};

export default canvasReducer;
